// theme.js
import { createTheme } from '@mui/material/styles';

// Paleta basada en el logotipo de izzi
const izziColors = {
  black: '#000000',
  aqua: '#00C4CC',
  orange: '#FF8C00',
  magenta: '#D40078',
  yellow: '#FFD400',
  white: '#FFFFFF',
};

/**
 * Opciones del tema de MUI. Los COLORES vienen del tema activo de la app
 * (variables --wb-mui-* de src/themes/<id>.css) vía buildMuiTheme; estos
 * valores son el respaldo (tests, o si la hoja no cargó).
 */
export const themeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: izziColors.black,
    },
    secondary: {
      main: izziColors.magenta,
    },
    info: {
      main: izziColors.aqua,
    },
    warning: {
      main: izziColors.orange,
    },
    success: {
      main: izziColors.yellow,
    },
    background: {
      default: izziColors.white,
      paper: '#F9F9F9',
    },
    text: {
      primary: izziColors.black,
      secondary: '#555',
    },
  },
  typography: {
    fontFamily: `'Inter', 'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif`,
    h6: {
      fontWeight: 600,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          paddingInline: 16,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: izziColors.white,
          boxShadow: 'none',
          borderBottom: `1px solid #ddd`,
        },
      },
    },
  },
};

const theme = createTheme(themeOptions);

const readToken = (styles, name, fallback) => (styles && styles.getPropertyValue(name).trim()) || fallback;

/**
 * Tema de MUI con los colores del tema de la app ACTIVO (lee las variables
 * --wb-mui-* ya resueltas en <html>). MUI necesita colores reales (calcula
 * hover/disabled con alpha), por eso no se le pasan var(...).
 */
export const buildMuiTheme = () => {
  const styles = typeof window !== 'undefined' && window.getComputedStyle
    ? window.getComputedStyle(document.documentElement)
    : null;
  const p = themeOptions.palette;
  return createTheme({
    ...themeOptions,
    palette: {
      mode: readToken(styles, '--wb-mui-mode', p.mode),
      primary: { main: readToken(styles, '--wb-mui-primary', p.primary.main) },
      secondary: { main: readToken(styles, '--wb-mui-secondary', p.secondary.main) },
      info: { main: readToken(styles, '--wb-mui-info', p.info.main) },
      warning: { main: readToken(styles, '--wb-mui-warning', p.warning.main) },
      success: { main: readToken(styles, '--wb-mui-success', p.success.main) },
      background: {
        default: readToken(styles, '--wb-mui-bg-default', p.background.default),
        paper: readToken(styles, '--wb-mui-bg-paper', p.background.paper),
      },
      text: {
        primary: readToken(styles, '--wb-mui-text-primary', p.text.primary),
        secondary: readToken(styles, '--wb-mui-text-secondary', p.text.secondary),
      },
    },
    components: {
      ...themeOptions.components,
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: readToken(styles, '--wb-mui-appbar-bg', izziColors.white),
            boxShadow: 'none',
            borderBottom: `1px solid ${readToken(styles, '--wb-mui-appbar-border', '#ddd')}`,
          },
        },
      },
    },
  });
};

export default theme;
